import React from 'react';
import {
  SegmentGroupSegmentGroupTypeEnum,
  SegmentGroup as SegmentGroupType,
} from '@kiwicom/openapi-sdk/mmb';
import { ItineraryStatus } from '@kiwicom/orbit-components/lib/Itinerary';
import { Status as OrbitItineraryStatus } from '@kiwicom/orbit-components/lib/Itinerary/ItineraryStatus/types.d';

import Segment from './Segment';
import ItineraryBadgeList from './ItineraryBadgeList';

type SegmentGroupProps = {
  segmentGroup: SegmentGroupType;
};

/**
 * Helper component for rendering list of Segments inside the Segment Group
 *
 * @param segmentGroup - Segment group object which is part of server-driven Itinerary
 *
 * @returns SegmentList component
 */
const SegmentList = ({ segmentGroup }: SegmentGroupProps) => {
  const { segments, footerBadges, segmentGroupType } = segmentGroup;
  return (
    <>
      {segments.map((segment, segmentIndex) => {
        const isLast = segmentIndex === segments.length - 1;
        return (
          <Segment
            // eslint-disable-next-line react/no-array-index-key
            key={segmentIndex}
            segment={segment}
            type={segmentGroupType}
            footerBadges={isLast ? footerBadges : undefined}
          />
        );
      })}
    </>
  );
};

/**
 * Component for rendering one segment group of Itinerary. It can contain segments and badges.
 * In case of schedule change (groupType is different than default) it will be elevated, have a colored border
 * and can contain a title at the top.
 *
 * @param segmentGroup - Segment group object which is part of server-driven Itinerary
 *
 * @returns SegmentGroup component
 */
const SegmentGroup = ({ segmentGroup }: SegmentGroupProps) => {
  if (segmentGroup.segmentGroupType === SegmentGroupSegmentGroupTypeEnum.Default) {
    return (
      <>
        <SegmentList segmentGroup={segmentGroup} />
        {segmentGroup.badges && <ItineraryBadgeList badges={segmentGroup.badges} />}
      </>
    );
  }
  const isSegmentGroupActionable = segmentGroup.segments.some(
    segment => segment.additionalInfoGroups,
  );
  return (
    <>
      <ItineraryStatus
        type={segmentGroup.segmentGroupType as OrbitItineraryStatus}
        label={segmentGroup.title}
        actionable={isSegmentGroupActionable}
      >
        <SegmentList segmentGroup={segmentGroup} />
      </ItineraryStatus>
      {segmentGroup.badges && <ItineraryBadgeList badges={segmentGroup.badges} />}
    </>
  );
};

export default SegmentGroup;
